import { apiFetch } from "./apiClient";

// Builds an Error carrying the API's message, status and body (e.g. 409 with the
// active cleaning session when the robot is already busy).
async function buildApiError(res, fallbackMessage) {
  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  const error = new Error(data?.error || fallbackMessage);
  error.status = res.status;
  error.data = data;
  return error;
}

export async function getCustomerDevices() {
  const res = await apiFetch("/api/v1/list-devices");
  if (!res.ok) {
    throw new Error("Failed to fetch devices");
  }
  return res.json();
}

// Commands are either 'start', 'stop', 'pause', 'resume', 'dock'
export async function sendDeviceControlCommand(
  deviceId,
  command,
  zoneId = null,
  cleaningConfig = null,
) {
  let res;

  if (zoneId) {
    const body = { command: command, deviceId: deviceId };
    if (cleaningConfig) {
      body.cleaningConfig = cleaningConfig;
    }
    res = await apiFetch(`/api/services/controls/clean-zone-group/${zoneId}`, {
      method: "POST",
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      throw await buildApiError(res, "Failed to fetch device data");
    }
  } else {
    const body = { deviceId: deviceId };
    if (cleaningConfig) {
      body.cleaningConfig = cleaningConfig;
    }
    res = await apiFetch(`/api/services/controls/${command}`, {
      method: "POST",
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      throw await buildApiError(res, "Failed to fetch device data");
    }
  }
  console.log("Response from control command API:", res);
  return res.json();
}

export async function sendMultiZoneControlCommand(
  deviceId,
  command,
  zoneIds,
  cleaningConfig = null,
) {
  const body = { command, deviceId, zoneIds };

  if (cleaningConfig) {
    body.cleaningConfig = cleaningConfig;
  }

  const res = await apiFetch(`/api/services/controls/clean-zone-groups`, {
    method: "POST",
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    throw await buildApiError(res, "Failed to send multi-zone control command");
  }

  return res.json();
}

// Shared cleaning session + allowed commands for the controls page.
// Called on page load and after every SSE reconnect (SSE does not replay missed events).
export async function getDeviceControlState(deviceId) {
  const res = await apiFetch(
    `/api/services/controls/state/${encodeURIComponent(deviceId)}`,
  );
  if (!res.ok) {
    throw await buildApiError(res, "Failed to load control state");
  }
  return res.json();
}

// Continue a held/interrupted cleaning session from its current zone
export async function continueCleaningSession(deviceId) {
  const res = await apiFetch(`/api/services/controls/session/continue`, {
    method: "POST",
    body: JSON.stringify({ deviceId }),
  });
  if (!res.ok) {
    throw await buildApiError(res, "Failed to continue cleaning session");
  }
  return res.json();
}

// End the current cleaning session (does not move the robot)
export async function endCleaningSession(deviceId) {
  const res = await apiFetch(`/api/services/controls/session/end`, {
    method: "POST",
    body: JSON.stringify({ deviceId }),
  });
  if (!res.ok) {
    throw await buildApiError(res, "Failed to end cleaning session");
  }
  return res.json();
}

export async function submitCustomerFeedback({
  deviceId,
  rating,
  comment = "",
}) {
  const res = await apiFetch(`/api/services/controls/customer-feedback`, {
    method: "POST",
    body: JSON.stringify({
      deviceId,
      rating,
      comment,
    }),
  });

  if (!res.ok) {
    throw new Error("Failed to submit customer feedback");
  }

  return res.json();
}
